/**
 * Service API - Pilotage Management
 * Centralise tous les appels vers le backend FastAPI.
 */

export const API_BASE = process.env.REACT_APP_API_URL || "https://pilotage-management-api-hpgrgeere0d8e7bp.francecentral-01.azurewebsites.net";

// ---------------------------------------------------------------------------
// Token provider — enregistré une fois par App.js au démarrage (voir
// setTokenProvider), appelé avant chaque requête pour attacher le token
// Entra de l'API TheCrowd (Authorization: Bearer ...). Évite de faire
// transiter l'instance MSAL / le compte dans chacune des fonctions
// exportées ci-dessous.
// ---------------------------------------------------------------------------
let _tokenProvider = null;

export function setTokenProvider(fn) {
  _tokenProvider = fn;
}

async function getAuthHeaders() {
  if (!_tokenProvider) return {};
  try {
    const token = await _tokenProvider();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

// Expose le token brut pour les composants qui ne passent pas par apiFetch
// (ex. OfficeViewer, qui fait son propre fetch() vers get-office-token).
export async function getAccessToken() {
  if (!_tokenProvider) return null;
  try {
    return await _tokenProvider();
  } catch {
    return null;
  }
}

async function apiFetch(path, options = {}) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Erreur inconnue" }));
    let message = error.detail || `Erreur ${response.status}`;
    // FastAPI renvoie parfois detail sous forme de liste d'erreurs de validation
    // (422) : [{ loc, msg, type }, ...]. On les convertit en texte lisible.
    if (Array.isArray(message)) {
      message = message.map((e) => e.msg || JSON.stringify(e)).join(" ; ");
    }
    throw new Error(message);
  }

  return response.json();
}


// --- Collaborateurs ---

// actifsOnly : true (defaut) = collaborateurs actifs uniquement, comme le
// backend par defaut ; false = actifs et inactifs (page Equipe).
export function getCollaborateurs(actifsOnly = true) {
  const qs = actifsOnly ? "" : "?actifs_only=false";
  return apiFetch(`/api/collaborateurs${qs}`);
}

export function getCollaborateur(id) {
  return apiFetch(`/api/collaborateurs/${id}`);
}

export function getCollaborateurByEmail(email) {
  return apiFetch(`/api/collaborateurs/email/${encodeURIComponent(email)}`);
}

export function createCollaborateur(data) {
  return apiFetch("/api/collaborateurs", { method: "POST", body: JSON.stringify(data) });
}

export function updateCollaborateur(id, data) {
  return apiFetch(`/api/collaborateurs/${id}`, { method: "PUT", body: JSON.stringify(data) });
}


// --- Profils ---

export function getProfils() {
  return apiFetch("/api/profils");
}

// --- Taches ---

export function getTaches(categorie = null) {
  const params = categorie ? `?categorie=${categorie}` : "";
  return apiFetch(`/api/taches${params}`);
}

export function getTache(id) {
  return apiFetch(`/api/taches/${id}`);
}

export function createTache(data) {
  return apiFetch("/api/taches", { method: "POST", body: JSON.stringify(data) });
}

export function updateTache(id, data) {
  return apiFetch(`/api/taches/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export function setTacheProfils(tacheId, profilIds) {
  return apiFetch(`/api/taches/${tacheId}/profils`, {
    method: "PUT",
    body: JSON.stringify({ profil_ids: profilIds }),
  });
}

// --- Feuilles de temps ---

export function getFeuilleTemps(collaborateurId, annee, semaine) {
  return apiFetch(`/api/feuilles-temps/collaborateur/${collaborateurId}/semaine/${annee}/${semaine}`);
}

export function getFeuilleTempsMois(collaborateurId, annee, mois) {
  return apiFetch(`/api/feuilles-temps/collaborateur/${collaborateurId}/mois/${annee}/${mois}`);
}

// Plage de dates (AAAA-MM-JJ, bornes incluses) : periode personnalisee de la fiche collaborateur
export function getFeuilleTempsPeriode(collaborateurId, debut, fin) {
  return apiFetch(`/api/feuilles-temps/collaborateur/${collaborateurId}/periode?debut=${debut}&fin=${fin}`);
}

export function saisirFeuilleTemps(data) {
  return apiFetch("/api/feuilles-temps/saisie", { method: "POST", body: JSON.stringify(data) });
}

// Refonte Projets : lignes de la grille d'une semaine (taches du profil,
// etapes de l'acteur principal, lignes deja saisies cette semaine-la)
export function getLignesSaisie(collaborateurId, annee, semaine) {
  return apiFetch(`/api/feuilles-temps/lignes/${collaborateurId}/${annee}/${semaine}`);
}

// Refonte Projets : etapes proposees par « Ajouter une ligne » (acteur secondaire)
export function getLignesAjoutables(collaborateurId) {
  return apiFetch(`/api/feuilles-temps/lignes-ajoutables/${collaborateurId}`);
}


// --- Projets ---

// Avec collaborateurId : controle de visibilite et droits de l'appelant (mes_droits)
function qsCollaborateur(collaborateurId) {
  return collaborateurId ? `?collaborateur_id=${collaborateurId}` : "";
}

export function getProjet(id, collaborateurId = null) {
  return apiFetch(`/api/projets/${id}${qsCollaborateur(collaborateurId)}`);
}

export function createProjet(data, collaborateurId = null) {
  return apiFetch(`/api/projets${qsCollaborateur(collaborateurId)}`, { method: "POST", body: JSON.stringify(data) });
}

export function updateProjet(id, data, collaborateurId = null) {
  return apiFetch(`/api/projets/${id}${qsCollaborateur(collaborateurId)}`, { method: "PUT", body: JSON.stringify(data) });
}


// --- Fichiers Projet ---

export function getFichiersProjet(projetId, collaborateurId = null) {
  return apiFetch(`/api/projets/${projetId}/fichiers${qsCollaborateur(collaborateurId)}`);
}

// rattachement (refonte Projets) : { etapeId } ou { jalonId }, optionnel
export async function uploadFichierProjet(projetId, file, collaborateurId = null, rattachement = {}) {
  const formData = new FormData();
  formData.append("fichier", file);

  const params = new URLSearchParams();
  if (collaborateurId) params.append("collaborateur_id", collaborateurId);
  if (rattachement.etapeId) params.append("etape_id", rattachement.etapeId);
  if (rattachement.jalonId) params.append("jalon_id", rattachement.jalonId);
  const qs = params.toString() ? `?${params}` : "";

  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/projets/${projetId}/fichiers${qs}`, {
    method: "POST",
    headers: { ...authHeaders },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Erreur inconnue" }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  return response.json();
}

export function openFichierProjet(projetId, fichierId, collaborateurId = null) {
  return apiFetch(`/api/projets/${projetId}/fichiers/${fichierId}/open${qsCollaborateur(collaborateurId)}`);
}

export function deleteFichierProjet(projetId, fichierId, collaborateurId = null) {
  return apiFetch(`/api/projets/${projetId}/fichiers/${fichierId}${qsCollaborateur(collaborateurId)}`, {
    method: "DELETE",
  });
}


// --- Refonte Projets : roles, etapes, jalons ---

// Chef, acteur principal, co-chefs et secondaires, enregistres d'un bloc
export function updateRolesProjet(projetId, roles, collaborateurId) {
  // roles : { chef_projet_id, acteur_principal_id, cochef_ids, secondaire_ids }
  return apiFetch(`/api/projets/${projetId}/roles?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify(roles),
  });
}

export function getEtapes(projetId, collaborateurId, inclureInactives = false) {
  const params = [`collaborateur_id=${collaborateurId}`];
  if (inclureInactives) params.push("inclure_inactives=true");
  return apiFetch(`/api/projets/${projetId}/etapes?${params.join("&")}`);
}

export function createEtape(projetId, data, collaborateurId) {
  return apiFetch(`/api/projets/${projetId}/etapes?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateEtape(etapeId, data, collaborateurId) {
  return apiFetch(`/api/etapes/${etapeId}?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function getJalons(projetId, collaborateurId) {
  return apiFetch(`/api/projets/${projetId}/jalons?collaborateur_id=${collaborateurId}`);
}

export function createJalon(projetId, data, collaborateurId) {
  return apiFetch(`/api/projets/${projetId}/jalons?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateJalon(jalonId, data, collaborateurId) {
  return apiFetch(`/api/jalons/${jalonId}?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function proposerJalon(jalonId, collaborateurId) {
  return apiFetch(`/api/jalons/${jalonId}/proposer?collaborateur_id=${collaborateurId}`, { method: "POST" });
}

export function confirmerJalon(jalonId, collaborateurId) {
  return apiFetch(`/api/jalons/${jalonId}/confirmer?collaborateur_id=${collaborateurId}`, { method: "POST" });
}

export function refuserJalon(jalonId, commentaire, collaborateurId) {
  return apiFetch(`/api/jalons/${jalonId}/refuser?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify({ commentaire }),
  });
}

export function deleteJalon(jalonId, collaborateurId) {
  return apiFetch(`/api/jalons/${jalonId}?collaborateur_id=${collaborateurId}`, { method: "DELETE" });
}


// --- Refonte Projets : Mon activite (Mes projets, Mes etapes) ---

// Projets dont je suis acteur principal ou secondaire ; par defaut en cours et en attente
export function getMesProjets(collaborateurId, inclureTermines = false) {
  const qs = inclureTermines ? "?inclure_termines=true" : "";
  return apiFetch(`/api/mes/projets/${collaborateurId}${qs}`);
}

// Etapes non terminees de mes projets, triees par echeance
export function getMesEtapes(collaborateurId) {
  return apiFetch(`/api/mes/etapes/${collaborateurId}`);
}


// --- Refonte Projets : Pilotage (vue manager) et import de fiche .docx ---

// perimetre : "equipe" (defaut) ou "tous" (directeurs)
export function getPilotageProjets(collaborateurId, perimetre = "equipe", inclureTermines = false) {
  const params = [`perimetre=${perimetre}`];
  if (inclureTermines) params.push("inclure_termines=true");
  return apiFetch(`/api/pilotage/projets/${collaborateurId}?${params.join("&")}`);
}

// Analyse d'une fiche projet .docx : previsualisation, rien n'est cree
export async function parseFicheDocx(file) {
  const formData = new FormData();
  formData.append("file", file);

  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/projets/import-docx/parse`, {
    method: "POST",
    headers: { ...authHeaders },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Erreur inconnue" }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  return response.json();
}

// Creation du projet importe : projet, etapes et roles en une transaction
export function confirmerImportFiche(payload, collaborateurId) {
  // payload : { projet, chef_projet_id, acteur_principal_id, cochef_ids, secondaire_ids }
  return apiFetch(`/api/projets/import-docx/confirmer?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}


// --- Accueil : applications (liens), gestion reservee aux directeurs ---

// Categories dans l'ordre, chacune avec ses liens (tous les utilisateurs)
export function getApplications() {
  return apiFetch("/api/applications");
}

export function createCategorieApplication(titre, collaborateurId) {
  return apiFetch(`/api/applications/categories?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify({ titre }),
  });
}

export function renameCategorieApplication(categorieId, titre, collaborateurId) {
  return apiFetch(`/api/applications/categories/${categorieId}?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify({ titre }),
  });
}

export function deleteCategorieApplication(categorieId, collaborateurId) {
  return apiFetch(`/api/applications/categories/${categorieId}?collaborateur_id=${collaborateurId}`, { method: "DELETE" });
}

// ids : identifiants de toutes les categories, dans le nouvel ordre
export function ordonnerCategoriesApplications(ids, collaborateurId) {
  return apiFetch(`/api/applications/categories/ordre?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify({ ids }),
  });
}

// ids : identifiants de tous les liens de la categorie, dans le nouvel ordre
export function ordonnerLiensApplications(categorieId, ids, collaborateurId) {
  return apiFetch(`/api/applications/categories/${categorieId}/ordre?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify({ ids }),
  });
}

// data : { categorie_id, nom, description, url, reseau_local }
export function createLienApplication(data, collaborateurId) {
  return apiFetch(`/api/applications/liens?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateLienApplication(lienId, data, collaborateurId) {
  return apiFetch(`/api/applications/liens/${lienId}?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteLienApplication(lienId, collaborateurId) {
  return apiFetch(`/api/applications/liens/${lienId}?collaborateur_id=${collaborateurId}`, { method: "DELETE" });
}


// --- Gestion utilisateurs : suivi quotidien des operations sur les comptes ---

// data : { date_jour: "AAAA-MM-JJ", creations, modifications, desactivations }
export function saisirSuiviOperations(data, collaborateurId) {
  return apiFetch(`/api/suivi-operations?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Cumul du jour (tous techniciens) et mes saisies de ce jour
export function getSuiviJour(dateJour, collaborateurId) {
  return apiFetch(`/api/suivi-operations/jour/${dateJour}?collaborateur_id=${collaborateurId}`);
}

export function deleteSuiviSaisie(saisieId, collaborateurId) {
  return apiFetch(`/api/suivi-operations/${saisieId}?collaborateur_id=${collaborateurId}`, { method: "DELETE" });
}

// jours : 7, 30 ou 90 (managers et directeurs)
export function getSuiviTableauDeBord(collaborateurId, jours = 30) {
  return apiFetch(`/api/suivi-operations/tableau-de-bord?collaborateur_id=${collaborateurId}&jours=${jours}`);
}


// --- Change Management ---

export function createChangeManagement(data) {
  return apiFetch("/api/change-management", { method: "POST", body: JSON.stringify(data) });
}

export function getChangeManagementDashboard(periode = "tout") {
  return apiFetch(`/api/change-management/dashboard?periode=${periode}`);
}


// --- Bibliotheques ---

export function getBibliotheques(collaborateurId) {
  return apiFetch(`/api/bibliotheques?collaborateur_id=${collaborateurId}`);
}

export function createBibliotheque(data) {
  return apiFetch("/api/bibliotheques", { method: "POST", body: JSON.stringify(data) });
}

export function getBibliothequeConfig(slug, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/config?collaborateur_id=${collaborateurId}`);
}

export function getBibliothequeArborescence(slug, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/arborescence?collaborateur_id=${collaborateurId}`);
}

export function getBibliothequeFichier(slug, path, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/fichiers/${path}?collaborateur_id=${collaborateurId}`);
}

export function updateBibliothequeFichier(slug, path, content, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/fichiers/${path}?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify({ content }),
  });
}

export function createBibliothequeFichier(slug, path, content, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/fichiers/${path}?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
}

export function deleteBibliothequeFichier(slug, path, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/fichiers/${path}?collaborateur_id=${collaborateurId}`, {
    method: "DELETE",
  });
}

export function getBibliothequeDroits(slug, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/droits?collaborateur_id=${collaborateurId}`);
}

export function updateBibliothequeDroits(slug, droits, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/droits?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify({ droits }),
  });
}

export function createDossierBibliotheque(slug, chemin, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/dossiers?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify({ chemin }),
  });
}

export function deleteDossierBibliotheque(slug, chemin, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/dossiers/${chemin}?collaborateur_id=${collaborateurId}`, {
    method: "DELETE",
  });
}

export function renameDossierBibliotheque(slug, chemin, nouveauNom, collaborateurId) {
  return apiFetch(`/api/bibliotheques/${slug}/dossiers/${chemin}?collaborateur_id=${collaborateurId}`, {
    method: "PUT",
    body: JSON.stringify({ nouveau_nom: nouveauNom }),
  });
}

export async function uploadFichierBibliotheque(slug, dossier, file, collaborateurId) {
  const formData = new FormData();
  formData.append("file", file);

  const params = new URLSearchParams({ collaborateur_id: collaborateurId });
  if (dossier) params.append("dossier", dossier);

  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/bibliotheques/${slug}/upload?${params}`, {
    method: "POST",
    headers: { ...authHeaders },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Erreur inconnue" }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  return response.json();
}


// --- Jira Dashboard Manager ---

export function getJiraDashboardCollaborateur(jiraName) {
  return apiFetch(`/api/jira/dashboard/collaborateur?jira_name=${encodeURIComponent(jiraName)}`);
}

export function getJiraDashboardManager(collaborateurId, periode) {
  return apiFetch(`/api/jira/dashboard/manager?collaborateur_id=${collaborateurId}&periode=${periode}`);
}

export function getJiraDashboardManagerCharge(collaborateurId, periode) {
  return apiFetch(`/api/jira/dashboard/manager/charge?collaborateur_id=${collaborateurId}&periode=${periode}`);
}

export function getJiraDashboardManagerCompletude(collaborateurId) {
  return apiFetch(`/api/jira/dashboard/manager/completude?collaborateur_id=${collaborateurId}`);
}


// --- Dashboard Manager (projets/alertes) ---

export function getDashboardManagerAlertes(collaborateurId) {
  return apiFetch(`/api/dashboard-manager/alertes?collaborateur_id=${collaborateurId}`);
}


// --- Conversation Projet (fil unifie : commentaires projet + commentaires etapes) ---

export function getConversationProjet(projetId, collaborateurId) {
  return apiFetch(`/api/projets/${projetId}/conversation?collaborateur_id=${collaborateurId}`);
}

export function createCommentaireProjet(projetId, contenu, collaborateurId) {
  return apiFetch(`/api/projets/${projetId}/conversation`, {
    method: "POST",
    body: JSON.stringify({ contenu, collaborateur_id: collaborateurId }),
  });
}

export function markConversationProjetLue(projetId, collaborateurId) {
  return apiFetch(`/api/projets/${projetId}/conversation/lu?collaborateur_id=${collaborateurId}`, {
    method: "POST",
  });
}

export function getConversationsListe(collaborateurId) {
  return apiFetch(`/api/projets/conversations/${collaborateurId}`);
}


// --- Creation Compte ---

export function envoyerCreationCompte(data) {
  return apiFetch("/api/creation-compte", { method: "POST", body: JSON.stringify(data) });
}


// --- Images Bibliotheques ---

/**
 * Upload une image dans bibliotheques/{slug}/images/ et retourne { sasUrl, blobPath }.
 * - sasUrl : URL SAS valide 1h pour affichage immédiat dans TipTap
 * - blobPath : chemin Blob persistant stocké dans le HTML de la fiche
 */
export async function uploadImageBibliotheque(slug, file, collaborateurId) {
  // 1. Upload dans images/
  const formData = new FormData();
  formData.append("file", file);

  const params = new URLSearchParams({
    collaborateur_id: collaborateurId,
    dossier: "images",
  });

  const response = await fetch(`${API_BASE}/api/bibliotheques/${slug}/upload?${params}`, {
    method: "POST",
    headers: { ...(await getAuthHeaders()) },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Erreur inconnue" }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  const uploaded = await response.json();
  // uploaded.path = "images/nom_fichier.png" (relatif au prefix bibliotheque)

  // 2. Obtenir l'URL SAS
  const blobPath = `bibliotheques/${slug}/${uploaded.path}`;
  const sasData = await apiFetch(
    `/api/bibliotheques/${slug}/images/sas?blob_path=${encodeURIComponent(blobPath)}&collaborateur_id=${collaborateurId}`
  );

  return { sasUrl: sasData.url, blobPath };
}

/**
 * Regénère les URLs SAS pour toutes les images d'un HTML.
 * Remplace les src "blob-img:bibliotheques..." par des URLs SAS fraîches.
 */
export async function resolveImagesSas(html, collaborateurId) {
  if (!html || !html.includes('blob-img:')) return html;

  // Format stocké : blob-img:bibliotheques/{slug}/images/{fichier}
  const regex = /src="blob-img:(bibliotheques\/([^/]+)\/[^"]+)"/g;
  const matches = [...html.matchAll(regex)];
  if (matches.length === 0) return html;

  // Dédupliquer les chemins
  const uniquePaths = [...new Set(matches.map(m => m[1]))];

  // Générer les SAS en parallèle
  const sasMap = {};
  await Promise.all(uniquePaths.map(async (blobPath) => {
    try {
      // blobPath = "bibliotheques/{slug}/images/fichier.png"
      // slug = parts[1]
      const parts = blobPath.split('/');
      const slug = parts[1];
      const sasData = await apiFetch(
        `/api/bibliotheques/${slug}/images/sas?blob_path=${encodeURIComponent(blobPath)}&collaborateur_id=${collaborateurId}`
      );
      sasMap[blobPath] = sasData.url;
    } catch {
      // En cas d'erreur, laisser le src tel quel
    }
  }));

  // Remplacer dans le HTML
  return html.replace(regex, (match, blobPath) => {
    const url = sasMap[blobPath];
    return url ? `src="${url}"` : match;
  });
}


// --- Comptes AD ---

export function getReferentielsAD() {
  return apiFetch("/api/comptes-ad/referentiels");
}

export function getOUCiblesAD(entite = null) {
  const params = entite ? `?entite=${encodeURIComponent(entite)}` : "";
  return apiFetch(`/api/comptes-ad/ou${params}`);
}

export function simulerCompteAD(profil, collaborateurId) {
  return apiFetch(`/api/comptes-ad/simulation?collaborateur_id=${collaborateurId}`, {
    method: "POST",
    body: JSON.stringify(profil),
  });
}

export function creerDemandeAD(data) {
  return apiFetch("/api/comptes-ad", { method: "POST", body: JSON.stringify(data) });
}

export function getDemandesAD(statut = null, entite = null, limite = 100) {
  const params = [`limite=${limite}`];
  if (statut) params.push(`statut=${statut}`);
  if (entite) params.push(`entite=${encodeURIComponent(entite)}`);
  return apiFetch(`/api/comptes-ad?${params.join("&")}`);
}

export function getDemandeAD(id) {
  return apiFetch(`/api/comptes-ad/${id}`);
}

export function validerDemandeAD(id, data) {
  return apiFetch(`/api/comptes-ad/${id}/valider`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function annulerDemandeAD(id, data) {
  return apiFetch(`/api/comptes-ad/${id}/annuler`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}


// --- Comptes AD : administration des referentiels ---

export function getTablesAdminAD() {
  return apiFetch("/api/comptes-ad-admin/tables");
}

export function getOptionsAdminAD(cle) {
  return apiFetch(`/api/comptes-ad-admin/options/${cle}`);
}

export function getValeursAdminAD(cle) {
  return apiFetch(`/api/comptes-ad-admin/valeurs/${cle}`);
}

export function getLignesAdminAD(cle, inactifs = false, recherche = null, filtre2 = null) {
  const params = [`inactifs=${inactifs}`];
  if (recherche) params.push(`recherche=${encodeURIComponent(recherche)}`);
  if (filtre2) params.push(`filtre2=${encodeURIComponent(filtre2)}`);
  return apiFetch(`/api/comptes-ad-admin/${cle}?${params.join("&")}`);
}

export function getValeursFiltreAdminAD(cle) {
  return apiFetch(`/api/comptes-ad-admin/${cle}/valeurs-filtre`);
}

export function getHistoriqueAdminAD(cle, ligneId) {
  return apiFetch(`/api/comptes-ad-admin/${cle}/${ligneId}/historique`);
}

export function creerLigneAdminAD(cle, valeurs, collaborateurId) {
  return apiFetch(`/api/comptes-ad-admin/${cle}`, {
    method: "POST",
    body: JSON.stringify({ valeurs, collaborateur_id: collaborateurId }),
  });
}

export function modifierLigneAdminAD(cle, ligneId, valeurs, collaborateurId) {
  return apiFetch(`/api/comptes-ad-admin/${cle}/${ligneId}`, {
    method: "PUT",
    body: JSON.stringify({ valeurs, collaborateur_id: collaborateurId }),
  });
}

export function desactiverLigneAdminAD(cle, ligneId, collaborateurId) {
  return apiFetch(
    `/api/comptes-ad-admin/${cle}/${ligneId}?collaborateur_id=${collaborateurId}`,
    { method: "DELETE" }
  );
}

export function reactiverLigneAdminAD(cle, ligneId, collaborateurId) {
  return apiFetch(`/api/comptes-ad-admin/${cle}/${ligneId}/reactiver`, {
    method: "POST",
    body: JSON.stringify({ valeurs: {}, collaborateur_id: collaborateurId }),
  });
}


// --- Comptes AD : demandes deposees par Odoo ---

export function getDemandesOdoo(inclureTraitees = false, typeFlux = null) {
  const params = [`inclure_traitees=${inclureTraitees}`];
  if (typeFlux) params.push(`type_flux=${typeFlux}`);
  return apiFetch(`/api/comptes-ad-odoo?${params.join("&")}`);
}

export function getFicheOdoo(blob) {
  return apiFetch(`/api/comptes-ad-odoo/fiche?blob=${encodeURIComponent(blob)}`);
}
